import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {compact, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {AnalyticsEvent} from 'analytics/Analytics';
import {getPurposesForFolderSection} from 'archive/ArchivePurposesUtil';
import {assert} from 'Asserts';
import {Capability, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isBiologicsOwnedItem} from 'auth/capabilities/getCapabilityFeatures';
import {isCapabilityEnabledForListing} from 'auth/capabilities/isCapabilityEnabledForListing';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ChemistryPreferences} from 'chemistry/ChemistryConstants';
import ChemSearchButton from 'chemistry/ChemSearchButton';
import ChemSearchChip from 'chemistry/ChemSearchChip';
import {extractMolQuery, getChemSearchFolderItemListingExtras} from 'chemistry/ChemSearchUtils';
import DataConnector from 'connect/DataConnector';
import {AlignmentSummary} from 'constants/AlignmentConstants';
import {FolderSection, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {REGISTRIES_FINDER_ID, Section} from 'constants/NavConstants';
import {BioentityAliasInfo, SchemaInterfaceExtendingSchema} from 'constants/RegistryConstants';
import {StorableSummary, StorableSummaryExtras} from 'constants/SampleConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderItemState from 'filestate/FolderItemState';
import * as FileUtil from 'folder-items/FileUtil';
import getConfig from 'getConfig';
import {
  ActionOrigin,
  ProcessClientOrigin,
  ProcessExecutionType,
  SearchSection,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import InventoryFileStore from 'inventory/InventoryFileStore';
import navigateToURL from 'lib/navigateToURL';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {LoadingState} from 'models/reduceLoadingState';
import {areAaPartFeaturesEnabledForAntibody} from 'molbio/aa-sequences/util/aminoAcidPartsUtil';
import {isProteinAlignment} from 'molbio/AlignmentUtil';
import {SortWidget, trackSortEvent, trackSortsApplied} from 'nav/analytics/SearchAnalytics';
import ExpandedItemTable, {ExpandedItemTableItemMetadata} from 'nav/expanded/ExpandedItemTable';
import ExpandedToolbar from 'nav/expanded/folder-toolbar/ExpandedToolbar';
import {makeColumnsForSortMenu} from 'nav/expanded/RenderExpandedListing';
import {AllConnectionSchemasQuery} from 'nav/navbar/__generated/AllConnectionSchemasQuery';
import {ALL_CONNECTION_SCHEMAS_QUERY} from 'nav/navbar/CreateMenu';
import NavStore from 'nav/NavStore';
import {AllAssaySchemasQuery} from 'nav/sidepanel/__generated/AllAssaySchemasQuery';
import {AllInventorySchemasQuery} from 'nav/sidepanel/__generated/AllInventorySchemasQuery';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import SidepanelListingSettingsDropdown from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import {AllProcessOutputSchemasQuery} from 'nav/sidepanel/listing/__generated/AllProcessOutputSchemasQuery';
import {AllProcessTaskSchemasQuery} from 'nav/sidepanel/listing/__generated/AllProcessTaskSchemasQuery';
import {AllStudySchemasQuery} from 'nav/sidepanel/listing/__generated/AllStudySchemasQuery';
import {ItemListingInterfacesQuery} from 'nav/sidepanel/listing/__generated/ItemListingInterfacesQuery';
import {
  ItemListingQuery,
  ItemListingQueryVariables,
} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {ItemListingSchemasQuery} from 'nav/sidepanel/listing/__generated/ItemListingSchemasQuery';
import {getContainerConcentrationFilter} from 'nav/sidepanel/listing/getContainerConcentrationFilter';
import {
  addAttributeValues,
  addPermissionsAndTags,
  filterInterfacesByType,
  idsToItemListingQueryParams,
  ItemListingLegacySchemasByType,
  queryResultsToItems,
  queryResultsToSchemas,
  SchemasFilterFunction,
} from 'nav/sidepanel/listing/ItemListingUtil';
import ListingToolbarArea from 'nav/sidepanel/listing/ListingToolbarArea';
import SecondHeaderRow from 'nav/sidepanel/listing/SecondHeaderRow';
import SelectedRowsCounter from 'nav/sidepanel/listing/SelectedRowsCounter';
import SidepanelItemList from 'nav/sidepanel/listing/SidepanelItemList';
import {ALL_ASSAY_SCHEMAS_QUERY, ALL_INVENTORY_SCHEMAS_QUERY} from 'nav/sidepanel/SidepanelQueries';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import ToggleDropdown from 'nav/ToggleDropdown';
import TemplateCollectionActions from 'notebook/entry-templates/collections/TemplateCollectionActions';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import preferences from 'Preferences';
import openWorkspaceForProcessItem from 'processes/dashboard/openWorkspaceForProcessItem';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import AssayStore from 'results/AssayStore';
import {SidepanelRun} from 'runs/sidepanel/RunsSidepanelConstants';
import {getTestDefinitionURL} from 'sample-tests/network/TestDefinitionDAO';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import * as StorableActions from 'samples/StorableActions';
import StorableStore from 'samples/StorableStore';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from 'schemas/schema-interface/fields/CustomSchemaInterfaceFieldsUtils';
import {Schema} from 'schemas/SchemaConstants';
import {isRequestSchema} from 'schemas/SchemaPredicates';
import AIAssistedSearchButton from 'search/ai-assisted-search/AIAssistedSearchButton';
import AlignmentCompareButton from 'search/blast/AlignmentCompareButton';
import AlignmentView from 'search/blast/AlignmentView';
import BLASTButton from 'search/blast/BLASTButton';
import BLASTChip from 'search/blast/BLASTChip';
import BlastViewControl from 'search/blast/BlastViewControl';
import {ColoringSchemeType} from 'search/blast/ColoringScheme';
import MultiquerySelector from 'search/blast/MultiquerySelector';
import ClearSearchButton from 'search/ClearSearchButton';
import {FilterOption} from 'search/filters/base/FilterComponents';
import {FilterGroup} from 'search/filters/base/FilterFormConstants';
import ListingFiltersContainer from 'search/filters/ListingFiltersContainer';
import {getFilterErrorForFilter} from 'search/filters/ValidateFilters';
import {isFilterValidForTypes} from 'search/FiltersByType';
import * as FilterUtil from 'search/FilterUtil';
import {availableGroupKeysForFilters} from 'search/groupKeysByType';
import QueryInput from 'search/QueryInput';
import SavedSearchHeader from 'search/saved-searches/SavedSearchHeader';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import SaveSearchButton from 'search/saved-searches/SaveSearchButton';
import ShareSearchButton from 'search/saved-searches/ShareSearchButton';
import {
  AsyncFetcherLocation,
  BaseItem,
  BLASTFilter,
  Filter,
  FilterKey,
  getNoResultsMessage,
  GroupByKey,
  isProcessTaskOrOutput,
  isStorableSummary,
  ListableGroup,
  ListableItem,
  LISTING_ERROR_MESSAGE,
  MultiValueFilter,
  Operator,
  SearchParams,
  SingleValueFilter,
  Sort,
  QuickFilterKey,
} from 'search/SearchConstants';
import {BulkActionContext, trackSelectedSearchResult} from 'search/searchTracking';
import {SEARCH_TYPE_BY_SECTION} from 'search/SearchTypes';
import * as SearchUtil from 'search/SearchUtil';
import SortDropdown from 'search/SortDropdown';
import {Sortable} from 'search/SortForm';
import {pickSortableColumns, pruneInvisibleSorts} from 'search/SortUtil';
import {SearchProps} from 'search/withSearching';
import EntityStore from 'stores/EntityStore';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Link from 'ui-library/Link';
import * as nyc from 'ui-library/NYC';
import ResultsPager from 'ui-library/ResultsPager';
import {RowSelectionMode, TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import {computeSelectedIdsForToggledRows} from 'ui-library/scrollable/ScrollableTableUtil';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import * as Modals from 'util/Modals';
import pickDefined from 'util/pickDefined';
import preserveShallowEquality from 'util/preserveShallowEquality';
import * as WorkflowActions from 'workflows/WorkflowActions';
import {openWorklistListing} from 'worklists/WorklistUIActions';

import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {CollectibleListingGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/CollectibleListingGraphQL';
import {TemplateCollectionListingGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionListingGraphQL';
import {TestDefinitionListingGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestDefinitionListingGraphQL';
import {TestOrderListingGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';
import {WorklistSummaryGraphQL} from '../../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import CompactListingBanner from './CompactListingBanner';
import CompactResultsPagerWrapper from './CompactResultsPagerWrapper';
import {SearchView} from './search-results/SearchResultsView';

export type ItemListingSchemaInterface = ItemListingInterfacesQuery['schemaInterfaces'][number];

export interface ItemListingProps {
  actionSource: ActionOrigin;
  activeItemId: string | null;
  availableSortKeys: Array<string>;
  availableTypes: Array<string>;
  defaultTypes?: Array<string>;
  availableQuickFilters?: Array<QuickFilterKey>;
  isTypeRequired?: boolean;
  additionalHeaderButtons?: JSX.Element | boolean | null;
  makeCrumbs?: (settingsComponents?: JSX.Element) => JSX.Element;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  enableToggleExpanded?: boolean;
  isExpanded: boolean;
  preservedFilterKeys: Array<FilterKey>;
  filtersToReset?: Array<Filter>;
  // ItemListing always has a primary TYPES filter
  // Filter options which should go before the type filter
  primaryFilterOptionsBeforeTypeFilter?: Array<FilterOption>;
  // Filter options which should go after the type filter
  extraPrimaryFilterOptions: Array<FilterOption>;
  /**
   * The name of the listing being rendered. If this listing is being rendered under a tab heading (i.e. if
   * you are also passing {@link sectionTabs}, this value should be the same as the id field of the
   * corresponding tab.
   */
  section: NavConstants.Section;
  /** Search session id, passed down to ExpandedToolbar to allow refreshing specific search session. Useful
   * for search views like study items that rely on an item id rather than just the section. */
  searchSessionId?: string;
  sectionTabs?: JSX.Element;
  sectionHeader?: JSX.Element;
  hideSectionTabsWhenExpanded?: boolean;
  searchResultsSubtext?: string | null;
  worklistSummary?: WorklistSummaryGraphQL | null;
  graphqlItems?: Array<BaseItem> | null;
  graphqlSchemasByAvailableType?: ItemListingLegacySchemasByType;
  schemaInterfaces?: Array<SchemaInterfaceExtendingSchema> | null;
  massageItems?: ((items: Array<ListableItem>) => Array<ListableItem>) | null;
  isItemDisabled?: (item: ListableItem) => boolean;
  getItemDisabledTooltipText?: (item: ListableItem) => string | undefined;
  onSaveOrder?: (orderedItemIds: Array<string>) => void;
  savedSearchSection?: SearchSection;
  activeSavedSearch?: SavedSearch;
  showMultiKeySortMenu?: boolean;
  defaultHiddenColumnKeys?: Array<string> | null;
  dataTestAttrs?: DataTestAttrs;
  renderToolbar?: (
    items: Array<ListableItem> | null,
    selectedIds: Array<string>,
    searchParams: SearchParams
  ) => JSX.Element | null;
  renderAdditionalToolbarButtons?: (
    items: Array<ListableItem> | null,
    selectedIds: Array<string>
  ) => JSX.Element | null;
  renderLeadingToolbarButtons?: (
    items: Array<ListableItem> | null,
    selectedIds: Array<string>
  ) => JSX.Element | null;
  getSecondaryFilterGroups?: (
    selectedSchema: Schema | null,
    navSection: NavConstants.Section,
    folderSection: FolderSection | null
  ) => Array<FilterGroup>;
  // If defined, applies a filter to the schemas visible in this listing. The parameter will already be
  // restricted to schemas applicable to availableTypes, so this is only needed if more restrictive
  // pruning is required.
  filterSchemas?: SchemasFilterFunction;
  disableOpenItem?: boolean;
  disableBLAST?: boolean;
  rowSelectionMode?: RowSelectionMode | null;
  alwaysShowSelector?: boolean;
  customSortables?: Array<Sortable>;
}

export interface EnhancedItemListingProps {
  items: Array<ListableItem> | null;
  groups: Array<ListableGroup> | null;
  onOpenFolder: (folder: StringIDFolder) => void;
  schemasByAvailableType: ItemListingLegacySchemasByType;
  filteredInterfacesByAvailableTypes: Array<SchemaInterfaceExtendingSchema>;
  bioentityAliasInfosByBioentityId?: {[bioentityId: string]: Array<BioentityAliasInfo> | undefined};
  storableSummaryExtras?: StorableSummaryExtras | null;
}

export function getDefaultSecondaryFilterGroups(
  selectedSchema: Schema | null,
  navSection: NavConstants.Section,
  folderSection: FolderSection | null
): Array<FilterGroup> {
  return [
    {
      name: 'General',
      filterOptions: compact([
        {filterKey: FilterKey.NAMES},
        {filterKey: FilterKey.CREATED_AT},
        {filterKey: FilterKey.MODIFIED_AT},
        {filterKey: FilterKey.API_IDS},
        BenchlingConfig.ENABLE_AUTHORS ? {filterKey: FilterKey.AUTHOR} : null,
        {filterKey: FilterKey.CREATOR},
        {filterKey: FilterKey.OWNER},
        {
          filterKey: FilterKey.ARCHIVE_PURPOSES,
          extraProps: {archivePurposes: getPurposesForFolderSection(folderSection)},
        },
        {filterKey: FilterKey.ANY_LINK_SCHEMA_FIELD},
        {
          filterKey: FilterKey.SCHEMA_FIELD,
          extraProps: {schemaFields: selectedSchema?.fields || []},
        },
        {filterKey: FilterKey.CUSTOM_TAG},
        {filterKey: FilterKey.NOTE_REFERENCE},
        {filterKey: FilterKey.STARRED},
        BenchlingConfig.STUDIES_ENABLE && navSection !== NavConstants.SECTIONS.STUDY_ITEMS
          ? {filterKey: FilterKey.IN_STUDY}
          : null,
        {filterKey: FilterKey.LINEAGE_PARENT_API_IDENTIFIER},
        {filterKey: FilterKey.PROCESS_WATCHER},
      ]),
    },
    {
      name: 'Review',
      filterOptions: licenses.hasLicense('LICENSE_REVIEWS')
        ? [
            {filterKey: FilterKey.REVIEW},
            {filterKey: FilterKey.REVIEW_STARTED},
            {filterKey: FilterKey.REVIEW_INITIAL_STARTED},
            {filterKey: FilterKey.REVIEW_COMPLETED},
          ]
        : [],
    },
    ...(BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES && licenses.hasLicense('LICENSE_REVIEWS')
      ? [
          {
            name: 'Project',
            filterOptions: [
              {filterKey: FilterKey.REVIEW_LIFECYCLE} satisfies FilterOption,
              {filterKey: FilterKey.REVIEW_LIFECYCLE_TYPE} satisfies FilterOption,
            ],
          },
        ]
      : []),
    ...(BenchlingConfig.STUDIES_ENABLE
      ? [
          {
            name: 'Study',
            filterOptions: [{filterKey: FilterKey.STUDY_PHASE} satisfies FilterOption],
          },
        ]
      : []),
    {
      name: 'Entity',
      filterOptions: compact([
        BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? {filterKey: FilterKey.ANNOTATION} : null,
        BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? {filterKey: FilterKey.ANNOTATION_BASES} : null,
        {filterKey: FilterKey.SEQUENCE_BASES},
        BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? {filterKey: FilterKey.OLIGO_HELM_DNA} : null,
        BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? {filterKey: FilterKey.OLIGO_HELM_RNA} : null,
        BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? {filterKey: FilterKey.MONOMER} : null,
        {filterKey: FilterKey.PROTEIN_AMINO_ACIDS},
        isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES})
          ? {filterKey: FilterKey.INGREDIENTS}
          : null,
        {filterKey: FilterKey.PART_SEQUENCE},
        {filterKey: FilterKey.RNA_PART_SEQUENCE},
        areAaPartFeaturesEnabledForAntibody() ? {filterKey: FilterKey.AA_PART_SEQUENCE} : null,
        {filterKey: FilterKey.TRANSCRIPTIONS},
        BenchlingConfig.MOLBIO_ENABLE_ALPHAFOLD_GA ? {filterKey: FilterKey.ALPHAFOLD_STRUCTURE} : null,
        ...(BenchlingConfig.REGISTRY_ENABLE_TRIAL_SAMPLES &&
        BenchlingConfig.SAMPLE_TESTS_ENABLE &&
        navSection === NavConstants.SECTIONS.STUDY_ITEMS
          ? [{filterKey: FilterKey.SAMPLE_DEFINITION} satisfies FilterOption]
          : []),
      ]),
    },
    {
      name: 'Entity Validation',
      filterOptions: [
        {filterKey: FilterKey.SCHEMA_VALIDATION_STATUS},
        {filterKey: FilterKey.SCHEMA_VALIDATION_ERROR_LABEL},
        {filterKey: FilterKey.REGISTRATION_STATUS},
      ],
    },
    {
      name: 'Inventory',
      filterOptions: [
        {filterKey: FilterKey.CONTAINER_QUANTITY},
        ...getContainerConcentrationFilter(),
        {filterKey: FilterKey.ANCESTOR_STORABLE},
        {filterKey: FilterKey.SAMPLE_RESTRICTION_ACCESS},
        {filterKey: FilterKey.SAMPLE_RESTRICTION_STATUS},
        {filterKey: FilterKey.SAMPLE_AUTHORIZED_USERS},
        {filterKey: FilterKey.SAMPLE_OWNERS},
        ...(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE
          ? [
              {filterKey: FilterKey.EXPIRATION_DATE} satisfies FilterOption,
              {filterKey: FilterKey.IS_EXPIRED} satisfies FilterOption,
            ]
          : []),
        ...(BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW
          ? [{filterKey: FilterKey.FREEZE_THAW_COUNT} satisfies FilterOption]
          : []),
      ],
    },
    {
      name: 'Location validation',
      filterOptions: [
        {filterKey: FilterKey.INVENTORY_VALIDATION_STATUS} satisfies FilterOption,
        {filterKey: FilterKey.INVENTORY_VALIDATION_ERROR_LABEL} satisfies FilterOption,
      ],
    },
    {
      name: 'Container contents',
      filterOptions: [
        {filterKey: FilterKey.CONTAINER_CONTENTS},
        {filterKey: FilterKey.CONTAINER_CONTENT_SCHEMA},
      ],
    },
    {
      name: 'Check-out',
      filterOptions: [
        {filterKey: FilterKey.CONTAINER_CHECK_OUT_STATUS},
        {filterKey: FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE},
      ],
    },
    {
      name: 'Workflow',
      filterOptions: [{filterKey: FilterKey.WORKFLOW_TEMPLATE}, {filterKey: FilterKey.WORKFLOW_STATUS}],
    },
    {
      name: getLegacyRequestObjectName(),
      filterOptions: compact([
        {filterKey: FilterKey.REQUEST_STATUS},
        {filterKey: FilterKey.REQUEST_ASSIGNEE},
        {
          filterKey: FilterKey.REQUEST_TASK_SCHEMA,
          extraProps: {
            selectedAssaySchema: selectedSchema && isRequestSchema(selectedSchema) ? selectedSchema : null,
          },
        },
        BenchlingConfig.REQUESTS_ENABLE_REQUESTOR ? {filterKey: FilterKey.REQUEST_REQUESTOR} : null,
        {filterKey: FilterKey.REQUEST_IDS},
      ]),
    },
    ...(BenchlingConfig.REQUESTS_V2_ENABLE && navSection !== NavConstants.SECTIONS.REQUESTS_V2_SUBMISSIONS
      ? [
          {
            name: 'Request',
            filterOptions: [
              {filterKey: FilterKey.REQUEST_V2_SUBMISSION_STATUS} satisfies FilterOption,
              {filterKey: FilterKey.REQUEST_V2_DEFINITION} satisfies FilterOption,
            ],
          },
        ]
      : []),
    {
      name: 'Workflow task group',
      filterOptions: [
        {filterKey: FilterKey.PROCESS_STATUS},
        {filterKey: FilterKey.PROCESS_EXECUTION_TYPE},
        ...(BenchlingConfig.REQUESTS_V2_ENABLE
          ? [{filterKey: FilterKey.REQUEST_V2_SUBMISSION} satisfies FilterOption]
          : []),
      ],
    },
    {
      name: 'Workflow task',
      filterOptions: [
        {filterKey: FilterKey.PROCESS},
        {filterKey: FilterKey.PROCESS_TASK_ASSIGNEE},
        {filterKey: FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES},
        {filterKey: FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE},
        {filterKey: FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY},
        {filterKey: FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES},
        {
          filterKey: FilterKey.ROOT_PROCESS_TASK_SCHEMA,
          extraProps: {schemaExecutionTypes: [ProcessExecutionType.FLOWCHART]},
        } satisfies FilterOption,
        {
          filterKey: FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS,
        } satisfies FilterOption,
      ],
    },
    {
      name: 'Flowchart',
      filterOptions: [
        {filterKey: FilterKey.ROOT_PROCESS},
        {filterKey: FilterKey.ROOT_PROCESS_CREATOR},
        {filterKey: FilterKey.ROOT_PROCESS_WATCHER},
      ],
    },
    // TODO (BNCH-103204): Replace this config flag check
    ...(BenchlingConfig.PROCEDURES_ENABLE
      ? [
          {
            name: processExecutionTypeDisplayName(
              ProcessClientOrigin.RECIPES,
              ProcessExecutionType.PROCEDURE
            ),
            filterOptions: [{filterKey: FilterKey.RECIPE_TEMPLATE} satisfies FilterOption],
          },
          {
            name: processExecutionTypeDisplayName(
              ProcessClientOrigin.RECIPES,
              ProcessExecutionType.PROCEDURE_METHOD
            ),
            filterOptions: [
              {filterKey: FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS} satisfies FilterOption,
              {filterKey: FilterKey.RECIPE_RUN_CONDITION_NAMES} satisfies FilterOption,
              {filterKey: FilterKey.RECIPE_RUN_REPLICATE_NAMES} satisfies FilterOption,
            ],
          },
          {
            name: processExecutionTypeDisplayName(
              ProcessClientOrigin.RECIPES,
              ProcessExecutionType.PROCEDURE_STEP
            ),
            filterOptions: [{filterKey: FilterKey.RECIPE_METHOD_NAME} satisfies FilterOption],
          },
        ]
      : []),
    ...(BenchlingConfig.PROCEDURES_ENABLE_ASSAYS
      ? [
          {
            name: processExecutionTypeDisplayName(ProcessClientOrigin.ASSAYS, ProcessExecutionType.PROCEDURE),
            filterOptions: [{filterKey: FilterKey.ASSAY_TEMPLATE} satisfies FilterOption],
          },
          {
            name: processExecutionTypeDisplayName(
              ProcessClientOrigin.ASSAYS,
              ProcessExecutionType.PROCEDURE_METHOD
            ),
            filterOptions: [
              {filterKey: FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS} satisfies FilterOption,
              {filterKey: FilterKey.ASSAY_RUN_CONDITION_NAMES} satisfies FilterOption,
              {filterKey: FilterKey.ASSAY_RUN_REPLICATE_NAMES} satisfies FilterOption,
            ],
          },
          {
            name: processExecutionTypeDisplayName(
              ProcessClientOrigin.ASSAYS,
              ProcessExecutionType.PROCEDURE_STEP
            ),
            filterOptions: [{filterKey: FilterKey.ASSAY_METHOD_NAME} satisfies FilterOption],
          },
        ]
      : []),
    {
      name: 'Connection',
      filterOptions: [{filterKey: FilterKey.CONNECTION_ADAPTER}, {filterKey: FilterKey.CONNECTION_GATEWAY}],
    },
    {
      name: 'Box/Plate',
      filterOptions: [
        {filterKey: FilterKey.GRID_EMPTY_CONTAINERS},
        {filterKey: FilterKey.GRID_EMPTY_POSITIONS},
      ],
    },
    ...(BenchlingConfig.SAMPLE_TESTS_ENABLE
      ? [
          {
            name: 'Test Order',
            filterOptions: [
              {filterKey: FilterKey.ASSAY_TEMPLATE} satisfies FilterOption,
              {filterKey: FilterKey.DILUTION_FACTOR} satisfies FilterOption,
              {filterKey: FilterKey.SAMPLE} satisfies FilterOption,
              {filterKey: FilterKey.SAMPLE_CONTAINER} satisfies FilterOption,
              ...(navSection === NavConstants.SECTIONS.STUDY_ITEMS
                ? [{filterKey: FilterKey.SAMPLE_DEFINITION} satisfies FilterOption]
                : []),
              {filterKey: FilterKey.TEST_STATUS} satisfies FilterOption,
              {filterKey: FilterKey.WORKSHEET} satisfies FilterOption,
            ],
          },
        ]
      : []),
    ...(BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE
      ? [
          {
            name: 'Templates',
            filterOptions: [{filterKey: FilterKey.TEMPLATE_COLLECTION} satisfies FilterOption],
          },
        ]
      : []),
    ...(BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING
      ? [
          {
            name: 'Publishing',
            filterOptions: [{filterKey: FilterKey.PUBLISHING_STATUS} satisfies FilterOption],
          },
        ]
      : []),
  ];
}

class ItemListingConnector extends DataConnector<ItemListingProps & SearchProps, EnhancedItemListingProps> {
  override stores = [AssayStore, InventoryFileStore, NavStore, SampleSchemaStore, StorableStore];

  enhanceProps: () => EnhancedItemListingProps;

  constructor(props: ItemListingProps & SearchProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  private handleOpenFolder = (folder: StringIDFolder): void => {
    this.dispatch(SidepanelReduxActions.listFolder(folder));
  };

  private uncachedEnhanceProps = (): EnhancedItemListingProps => {
    const schemasForAllTypes = this.props.graphqlSchemasByAvailableType || {};
    if (!this.props.availableTypes.every((type) => Object.keys(schemasForAllTypes).includes(type))) {
      throw new Error('props.availableTypes contains an unsupported model type');
    }
    const schemasByAvailableType = pickDefined(schemasForAllTypes, ...this.props.availableTypes);

    const schemaInterfaces = this.props.schemaInterfaces || [];
    const filteredInterfacesByAvailableTypes = filterInterfacesByType(
      schemaInterfaces,
      this.props.availableTypes
    );

    if (!this.props.searchResults || !this.props.graphqlItems) {
      return {
        items: null,
        groups: null,
        onOpenFolder: this.handleOpenFolder,
        schemasByAvailableType,
        filteredInterfacesByAvailableTypes,
      };
    }

    const ids = this.props.graphqlItems.map((item) => item.id);
    const summariesById = _.indexBy(this.props.graphqlItems, 'id');
    const permissionsByItemId = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.PERMISSIONS_BY_ITEM_ID,
      ids
    );
    // PERMISSIONS_BY_ITEM_ID don't need to be re-fetched because they are already fetched with the search request.
    if ([...this.props.graphqlItems, ...Object.values(permissionsByItemId)].some((model) => !model)) {
      return {
        items: null,
        groups: null,
        onOpenFolder: this.handleOpenFolder,
        schemasByAvailableType,
        filteredInterfacesByAvailableTypes,
      };
    }
    let items = ids.map((id) => addPermissionsAndTags(summariesById[id], permissionsByItemId));
    if (this.props.massageItems) {
      items = this.props.massageItems(items);
    }

    const itemsById = _.indexBy(items, 'id');
    const groups: Array<ListableGroup> =
      this.props.searchResults?.groups?.map((groupResult) => ({
        id: groupResult.key,
        label: groupResult.label ? groupResult.label : '(None)',
        count: groupResult.count,
        items: groupResult.childItems.flatMap((itemResult) => {
          const item = itemsById[itemResult.id];
          return item ? [addAttributeValues(item, itemResult)] : [];
        }),
        aggregates: groupResult.aggregates,
        isExpanded: this.props.expandedItemIds.includes(groupResult.key),
        groupItem: itemsById[groupResult.key],
        areChildrenLoading: this.props.childrenLoadingIds.includes(groupResult.key),
      })) || [];

    // TODO(Search): This conditional is only required because itemResults isn't returned from every search
    // API. Once we either update those APIs to return the attribute, or consolidate on a single endpoint,
    // we can remove the conditional and replace with the first block entirely.
    let topLevelItems: Array<ListableItem>;
    if (this.props.searchResults.itemResults) {
      topLevelItems = this.props.searchResults.itemResults.flatMap((itemResult) => {
        const item = itemsById[itemResult.id];
        return item ? [addAttributeValues(item, itemResult)] : [];
      });
    } else {
      topLevelItems = this.props.searchResults.ids.flatMap((id) => {
        const item = itemsById[id];
        return item ? [item] : [];
      });
    }

    const bioentityIds = items.flatMap((item) => (FileUtil.isInventoryFile(item) ? [item.id] : []));
    const bioentityAliasInfosByBioentityId = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID,
      bioentityIds
    );
    if (!this.props.asyncFetcherIsFetchedState[AsyncFetcherLocation.ITEM_LISTING_ENHANCE_PROPS]) {
      this.forceFetchMultiple(ManagedModelType.BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID, bioentityIds);
    }

    const storableIds = items.flatMap((item) => (isStorableSummary(item) ? [item.id] : []));
    const storableSummaryExtras = this.storableStore().getStorableSummaryExtras();
    if (
      !StorableSummaryExtrasManager.areKeysLoadedOrLoading(storableIds) ||
      !this.props.asyncFetcherIsFetchedState[AsyncFetcherLocation.ITEM_LISTING_ENHANCE_PROPS]
    ) {
      for (const id of storableIds) {
        StorableSummaryExtrasManager.requestFetch(id, {forceFetch: true});
      }
    }

    return {
      items: topLevelItems,
      groups,
      onOpenFolder: this.handleOpenFolder,
      schemasByAvailableType,
      filteredInterfacesByAvailableTypes,
      bioentityAliasInfosByBioentityId,
      storableSummaryExtras,
    };
  };
}

interface ItemListingState {
  shouldHighlightSubstructure: boolean;
  zoom: number;
  coloringScheme: ColoringSchemeType;
  comparing: boolean;
  isSecondaryDropdownOpen: boolean;
}

export class ItemListing extends React.PureComponent<
  ItemListingProps & SearchProps & EnhancedItemListingProps,
  ItemListingState
> {
  private getItemMetadataById = preserveShallowEquality(
    (items: Array<ListableItem>): {[itemId: string]: ExpandedItemTableItemMetadata} =>
      objectFromEntries(
        items.map((item) => {
          const isDisabled = this.props.isItemDisabled?.(item) ?? false;
          return [
            item.id,
            {
              itemId: item.id,
              isDisabled,
              isSelected: this.props.selectedIds.includes(item.id),
              disabledTooltipText: isDisabled ? this.props.getItemDisabledTooltipText?.(item) : undefined,
            },
          ];
        })
      )
  );

  constructor(props: ItemListingProps & SearchProps & EnhancedItemListingProps) {
    super(props);
    this.state = {
      shouldHighlightSubstructure:
        preferences.get(ChemistryPreferences.SHOULD_HIGHLIGHT_SUBSTRUCTURE) ?? true,
      zoom: 0,
      coloringScheme: ColoringSchemeType.Mismatch,
      comparing: false,
      isSecondaryDropdownOpen: false,
    };
  }

  override componentDidUpdate(prevProps: ItemListingProps & SearchProps & EnhancedItemListingProps): void {
    const current = this.props.asyncFetcherIsFetchedState[AsyncFetcherLocation.ITEM_LISTING_ENHANCE_PROPS];
    if (!current && this.props.items != null) {
      this.props.onUpdateAsyncFetcherIsFetchedState(AsyncFetcherLocation.ITEM_LISTING_ENHANCE_PROPS, true);
    }
  }

  private handleSetSecondaryDropdownOpen = (isSecondaryDropdownOpen: boolean): void => {
    this.setState({isSecondaryDropdownOpen});
  };

  private getAnalyticsMetadataForItem = (item: ListableItem): {[key: string]: string | null} => {
    const analyticsMetadata: {[key: string]: string | null} = {action_origin: this.props.actionSource};
    if (APIUtil.isRun(item.id)) {
      analyticsMetadata.connection_id = (item as SidepanelRun).connection?.id ?? null;
      analyticsMetadata.run_id = (item as SidepanelRun).warehouseId;
      analyticsMetadata.product = 'lab_automation';
    } else if (APIUtil.isConnection(item.id)) {
      analyticsMetadata.adapter_type = (item as ConnectionListingGraphQL).adapterId;
    }
    return analyticsMetadata;
  };

  private handleOpenItem = (item: ListableItem): void => {
    if (this.props.disableOpenItem) {
      return;
    }

    trackSelectedSearchResult(item.id, this.props.searchResults);

    if (APIUtil.apiIdToMaybeType(item.id) === ModelTypeConstants.FOLDER) {
      this.props.onOpenFolder(item as StringIDFolder);
    } else if (APIUtil.isStorable(item.id)) {
      StorableActions.open(item as StorableSummary);
    } else if (APIUtil.apiIdToMaybeType(item.id) === ModelTypeConstants.WORKFLOW) {
      WorkflowActions.routeToWorkflow(item.id);
    } else if (APIUtil.isSequenceAnalysis(item.id) || APIUtil.isProteinAlignment(item.id)) {
      const alignment = item as AlignmentSummary;
      FileBrowserActions.openItem(
        isProteinAlignment(alignment) ? alignment.protein.id : alignment.sequence.id,
        {
          alignment: alignment.id,
        }
      );
    } else if (isProcessTaskOrOutput(item)) {
      openWorkspaceForProcessItem(item);
    } else if (APIUtil.isWorklist(item.id)) {
      openWorklistListing(item as WorklistSummaryGraphQL);
    } else if (APIUtil.isTemplateCollection(item.id)) {
      TemplateCollectionActions.openCollection(item as TemplateCollectionListingGraphQL);
    } else if (APIUtil.isTemplateCollectible(item.id)) {
      TemplateCollectionActions.openCollectible(item as CollectibleListingGraphQL);
    } else if (APIUtil.apiIdToMaybeType(item.id) === ModelTypeConstants.TEST_ORDER) {
      const testOrder = item as TestOrderListingGraphQL;
      FileBrowserActions.openFile(testOrder.sample.id, {
        analyticsMetadata: this.getAnalyticsMetadataForItem(item),
        onOpen: (fileState: FolderItemState) => {
          fileState.activateTestOrdersTab();
        },
      });
    } else if (APIUtil.apiIdToMaybeType(item.id) === ModelTypeConstants.TEST_DEFINITION) {
      const testDefinition = item as TestDefinitionListingGraphQL;
      navigateToURL(getTestDefinitionURL(testDefinition.id));
    } else if (APIUtil.apiIdToMaybeType(item.id) === ModelTypeConstants.REACTION) {
      // Reactions don't have their own detail page — navigate to the parent entry instead.
      const reaction = item as {entry?: {__typename: string; id: string} | null};
      const entryId = reaction.entry?.__typename === 'EntryNode' ? reaction.entry.id : null;
      if (entryId) {
        FileBrowserActions.openFile(entryId, {
          analyticsMetadata: this.getAnalyticsMetadataForItem(item),
        });
      }
    } else {
      FileBrowserActions.openFile(item.id, {
        analyticsMetadata: this.getAnalyticsMetadataForItem(item),
        isBiologicsOwned: isBiologicsOwnedItem(item),
      });
    }
  };

  private handleSetQuery = (query: string): void => {
    this.props.onUpdateQueryString(query);
  };

  private handleChangeFilters = (filters: Array<Filter>): void => {
    this.props.onUpdateFilters(filters);
  };

  private handleSetSortOrder = (sorts: Array<Sort>): void => {
    this.props.onUpdateSortOrder(sorts);
  };

  private handleSetPageSize = (limit: number): void => {
    this.props.onUpdateMultiqueryBlastOffset({});
    this.props.onUpdatePageSize(limit);
  };

  private handleSetOffset = (offset: number): void => {
    if (this.props.listingState.groupBySelection) {
      const group = this.props.groups?.find((obj) => obj.id === this.props.listingState.groupBySelection);
      assert(group != null, 'The groupBySelection does not exist in the groups', {
        groupBySelection: this.props.listingState.groupBySelection,
        groupIds: this.props.groups?.map((obj) => obj.id),
      });

      if (group.items.length < offset + (this.props.childPageSize ?? 0)) {
        this.props.onRequestMoreChildItems(this.props.listingState.groupBySelection);
      }

      this.props.onUpdateMultiqueryBlastOffset({
        ...this.props.listingState.multiqueryBlastOffset,
        [this.props.listingState.groupBySelection]: offset,
      });
    } else {
      this.props.onUpdateOffset(offset);
    }
  };

  /**
   * Return the list of filters keys configurable in this view. These are filters that AI-assisted
   * search considers eligible for selection.
   */
  private getAvailableFilterKeys(
    primaryFilterOptions: Array<FilterOption>,
    secondaryFilterGroups: Array<FilterGroup>
  ): Array<FilterKey> {
    return [
      ...primaryFilterOptions.map((filterOption) => filterOption.filterKey),
      ...secondaryFilterGroups.flatMap((filterGroup) =>
        filterGroup.filterOptions.map((filterOption) => filterOption.filterKey)
      ),
    ];
  }

  private getClearedSearchParams(): SearchParams {
    const filtersToReset = this.props.filtersToReset || [
      {
        key: FilterKey.TYPES,
        operator: Operator.IS_ONE_OF,
        value: this.props.defaultTypes || this.props.availableTypes,
      },
    ];
    return SearchUtil.clearSearchParams(this.props.searchParams, {
      preservedFilterKeys: this.props.preservedFilterKeys,
      preserveLockedFilters: true,
      filtersToReset,
    });
  }

  private handleClear = (): void => {
    this.props.onSearch(this.getClearedSearchParams());
    if (this.props.activeSavedSearch) {
      // Clear saved search
      this.props.onSelectSavedSearch(undefined);
    }
  };

  private handleSelectSavedSearch = async (savedSearch?: SavedSearch): Promise<void> => {
    if (!savedSearch) {
      this.handleClear();
    } else {
      await this.props.onSelectSavedSearch(savedSearch);
    }
  };

  private handleToggleSelectedIds = (toggledIds: Array<string>, isSelected: boolean): void => {
    const newSelectedIds = computeSelectedIdsForToggledRows(
      this.props.selectedIds,
      toggledIds,
      isSelected,
      this.props.rowSelectionMode
    );
    this.props.onSelectIds(newSelectedIds);
  };

  private isClearEnabled = (): boolean =>
    this.props.showMultiKeySortMenu
      ? SearchUtil.hasQueryOrNonDefaultParameters(
          this.props.searchParams,
          this.props.preservedFilterKeys,
          this.props.defaultTypes || this.props.availableTypes,
          SEARCH_TYPE_BY_SECTION[this.props.section]
        )
      : SearchUtil.hasQueryOrNonDefaultFilters(
          this.props.searchParams,
          this.props.preservedFilterKeys,
          this.props.defaultTypes || this.props.availableTypes
        );

  private getEmptyMessage = (
    typesFilter: MultiValueFilter,
    interfaceFilter?: MultiValueFilter | null
  ): string =>
    this.props.loadingState === LoadingState.FAILED
      ? LISTING_ERROR_MESSAGE
      : getNoResultsMessage(typesFilter, interfaceFilter);

  private trackSortsApplied = (sortWidget: SortWidget, sorts: Array<Sort>): void => {
    const metadata = {
      sortWidget,
      section: this.props.section,
      isExpanded: this.props.isExpanded,
      searchParams: {
        ...this.props.searchParams,
        offset: 0,
        sorts,
      },
    };
    trackSortsApplied(metadata);
  };

  private handleSetColumnHeaderSort = (sort: Sort): void => {
    const newSorts = [sort];
    this.handleSetSortOrder(newSorts);
    this.trackSortsApplied(SortWidget.COLUMN_HEADER, newSorts);
  };

  private handleToggleExpandedItem = (id: string, isExpanded: boolean): void => {
    this.props.onToggleItemsExpanded([id], isExpanded);
  };

  private setShouldHighlightSubstructure = (shouldHighlightSubstructure: boolean): void => {
    preferences.set(ChemistryPreferences.SHOULD_HIGHLIGHT_SUBSTRUCTURE, shouldHighlightSubstructure);
    this.setState({shouldHighlightSubstructure});
  };

  setZoom = (zoom: number): void => {
    this.setState({zoom});
  };

  setColoringScheme = (coloringScheme: ColoringSchemeType): void => {
    this.setState({coloringScheme});
  };

  setComparing = (comparing: boolean): void => {
    this.setState({comparing});
  };

  private renderExpandAllButton(hasBLASTFilter: boolean): JSX.Element | null {
    const groups = this.props.groups;
    if (!groups?.length || hasBLASTFilter) {
      return null;
    }

    const shouldExpand = this.props.expandedItemIds.length === 0;
    return (
      <div style={{marginLeft: 4, paddingLeft: 8, borderLeft: `1px solid ${GRAY}`}}>
        <Link
          onClick={async () => {
            const anyGroupsNotYetFetched = groups.some((g) => g.count > 0 && g.items.length === 0);
            if (shouldExpand && groups.length > 100 && anyGroupsNotYetFetched) {
              const confirmed = await Modals.confirm(
                'Expand all',
                'This action may take up to several minutes because it will return a large number of items. We recommend filtering to 100 groups or less when expanding all groups.',
                ButtonStyle.SECONDARY,
                {acceptText: 'Continue', promiseCompatible: true, enableRedesign: true}
              );
              if (!confirmed) {
                return;
              }
            }
            this.props.onToggleItemsExpanded(
              groups.map((g) => g.id),
              shouldExpand
            );
          }}
        >
          {shouldExpand ? 'Expand all groups' : 'Collapse all groups'}
        </Link>
      </div>
    );
  }

  override render(): JSX.Element {
    const isClearEnabled = this.isClearEnabled();
    const isSearch = isClearEnabled || this.props.section === NavConstants.Section.SEARCH;
    const isSignedIn = EntityStore.currentUser() != null;
    const sectionFilter = FilterUtil.getFilterByKey(
      this.props.searchParams.filters,
      FilterKey.FOLDER_SECTION
    ) as SingleValueFilter | null;
    const folderSection = sectionFilter ? (sectionFilter.value as FolderSection) : null;
    const finderId = NavConstants.SECTION_TO_FINDER_ID[this.props.section];
    const typesFilter = FilterUtil.getFilterByKey(
      this.props.searchParams.filters,
      FilterKey.TYPES
    ) as MultiValueFilter;
    const interfaceFilter = FilterUtil.getFilterByKey(
      this.props.searchParams.filters,
      FilterKey.SCHEMA_INTERFACE
    ) as MultiValueFilter | null;
    const folderId = (FilterUtil.getFilterValueByKey(this.props.searchParams.filters, FilterKey.FOLDER) ||
      FilterUtil.getFilterValueByKey(this.props.searchParams.filters, FilterKey.ANCESTOR_FOLDER)) as
      | string
      | null;
    const selectedSchema = FilterUtil.getSelectedSchema(
      this.props.searchParams.filters,
      Object.values(this.props.schemasByAvailableType)
        .flatMap((schemasForType) => schemasForType)
        .concat(this.props.schemaInterfaces || [])
    );

    // This will always be null when using the default filters, but can be set if custom filter overrides
    // are configured.
    const collectionFilter = FilterUtil.getFilterByKey(
      this.props.searchParams.filters,
      FilterKey.REGISTRY_COLLECTION_OWNER
    ) as SingleValueFilter | null;
    const includeTypesFilter =
      this.props.availableTypes.length === 1 &&
      this.props.schemasByAvailableType[this.props.availableTypes[0]].length === 0;
    const primaryFilterOptions = [
      ...(this.props.primaryFilterOptionsBeforeTypeFilter ?? []),
      // Don't display TYPES filter if there is only one available type with no schemas to choose from
      ...(includeTypesFilter
        ? []
        : [
            {
              filterKey: FilterKey.TYPES,
              extraProps: {
                availableTypes: this.props.availableTypes,
                filteredInterfacesByAvailableTypes: this.props.filteredInterfacesByAvailableTypes,
                isTypeRequired: this.props.isTypeRequired ?? false,
                filterSchemas: this.props.filterSchemas,
              },
            } as FilterOption,
          ]),
      ...this.props.extraPrimaryFilterOptions,
    ];

    // We need the full list of items, including group children, to determine which features to display,
    // so concat that list (e.g. used to determine what items are displayed in the toolbar.)
    const itemsWithGroupChildren = [
      ...(this.props.items ?? []),
      ...(this.props.groups?.flatMap((group) => group.items) ?? []),
    ];
    const itemMetadataById = this.getItemMetadataById(itemsWithGroupChildren);

    let sortableColumns: Array<Sortable> = [];
    if (this.props.items) {
      const columns = makeColumnsForSortMenu(
        this.props.items,
        this.props.searchParams.sorts,
        {
          bioentityAliasInfosByBioentityId: this.props.bioentityAliasInfosByBioentityId,
          isRegistry: finderId === REGISTRIES_FINDER_ID,
          section: folderSection,
          storableSummaryExtras: this.props.storableSummaryExtras,
          runSchemas: this.props.schemasByAvailableType[
            ModelTypeConstants.ASSAY_RUN
          ] as Array<AssayRunSchema>,
        },
        typesFilter != null ? typesFilter.value : [],
        this.props.groups ?? []
      );
      sortableColumns = pickSortableColumns(columns);
    }
    const sortDropdown = (
      <SortDropdown
        onChangeSorts={(sorts: Array<Sort>) => {
          this.handleSetSortOrder(sorts);
          this.trackSortsApplied(SortWidget.SORT_DROPDOWN, sorts);
        }}
        columns={sortableColumns}
        appliedSorts={pruneInvisibleSorts(this.props.searchParams.sorts)}
        isLoading={this.props.items == null}
        trackSortEvent={(event: AnalyticsEvent) => {
          const metadata = {
            sortWidget: SortWidget.SORT_DROPDOWN,
            section: this.props.section,
            isExpanded: this.props.isExpanded,
          };
          trackSortEvent(event, metadata);
        }}
        addSortsDisabled={false}
        customSortables={this.props.customSortables}
      />
    );
    const saveSearchButton =
      !this.props.activeSavedSearch &&
      this.props.savedSearchSection &&
      // TODO(Search): Rename this to better reflect usage.
      isClearEnabled ? (
        <SaveSearchButton
          searchSection={this.props.savedSearchSection}
          activeSavedSearch={this.props.activeSavedSearch}
          onSaved={this.handleSelectSavedSearch}
          searchParams={this.props.searchParams}
        />
      ) : undefined;

    const shareSearchButton =
      getConfig('ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH') &&
      this.props.activeSavedSearch &&
      this.props.savedSearchSection &&
      this.props.isExpanded ? (
        <ShareSearchButton
          searchSection={this.props.savedSearchSection}
          activeSavedSearch={this.props.activeSavedSearch}
          onSaved={this.handleSelectSavedSearch}
          onDeleted={() => {
            this.props.onSelectSavedSearch(undefined);
          }}
        />
      ) : undefined;

    const hasBLASTCapability =
      isCapabilityEnabledForListing(Capability.MOLBIO) || isCapabilityEnabledForListing(Capability.REGISTRY);
    const enableBLAST =
      isFilterValidForTypes(FilterKey.BLAST, this.props.availableTypes) &&
      BenchlingConfig.ENABLE_BLAST_SEARCH &&
      hasBLASTCapability &&
      !this.props.disableBLAST;

    const blastFilter = FilterUtil.getFilterByKey(
      this.props.searchParams.filters,
      FilterKey.BLAST
    ) as BLASTFilter | null;
    const hasBLASTFilter = Boolean(blastFilter);
    const isBLASTMultiQuery = hasBLASTFilter && this.props.groups != null && this.props.groups.length > 0;
    const isBLASTMultiQueryLoadingChildren = isBLASTMultiQuery && this.props.childrenLoadingIds.length > 0;

    const blastButton = isFilterValidForTypes(FilterKey.BLAST, typesFilter?.value ?? []) ? (
      <BLASTButton
        className={classNames(css(itemListingStyle.queryInputSearchButton))}
        onUpdateFilters={this.handleChangeFilters}
        searchParams={this.props.searchParams}
        key="BLASTButton"
      />
    ) : null;

    const blastInputIcons =
      blastFilter != null ? (
        <span key="blastChip" className={css(itemListingStyle.queryInputSearchChip)}>
          <BLASTChip
            onUpdateFilters={this.handleChangeFilters}
            searchParams={this.props.searchParams}
            BLASTFilter={blastFilter}
            filterError={getFilterErrorForFilter(
              this.props.searchParams.filters,
              this.props.filterErrors,
              FilterKey.BLAST
            )}
          />
        </span>
      ) : (
        blastButton
      );

    // Show the chem search chip in listings that can include entities, and hide for other uses.
    const enableChemSearch =
      isFilterValidForTypes(FilterKey.CHEMICAL_STRUCTURE, this.props.availableTypes) &&
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE;
    const chemicalStructureQuery = extractMolQuery(this.props.searchParams);

    const chemSearchButton = isFilterValidForTypes(FilterKey.CHEMICAL_STRUCTURE, typesFilter?.value ?? []) ? (
      <ChemSearchButton
        className={classNames(css(itemListingStyle.queryInputSearchButton))}
        onUpdateFilters={this.handleChangeFilters}
        searchParams={this.props.searchParams}
        key="chemSearchButton"
        toggleHighlightParams={{
          showToggleHighlightOption: true,
          shouldHighlightSubstructure: this.state.shouldHighlightSubstructure,
          setShouldHighlightSubstructure: this.setShouldHighlightSubstructure,
        }}
      />
    ) : null;

    const chemSearchInputIcon =
      chemicalStructureQuery != null ? (
        <span key="chemSearchChip" className={css(itemListingStyle.queryInputSearchChip)}>
          <ChemSearchChip
            onUpdateFilters={this.handleChangeFilters}
            searchParams={this.props.searchParams}
            toggleHighlightParams={{
              showToggleHighlightOption: true,
              shouldHighlightSubstructure: this.state.shouldHighlightSubstructure,
              setShouldHighlightSubstructure: this.setShouldHighlightSubstructure,
            }}
            mol={chemicalStructureQuery}
            filterError={getFilterErrorForFilter(
              this.props.searchParams.filters,
              this.props.filterErrors,
              FilterKey.CHEMICAL_STRUCTURE
            )}
          />
        </span>
      ) : (
        chemSearchButton
      );
    const chemSearchFolderItemListingExtras = getChemSearchFolderItemListingExtras(
      this.props.searchParams,
      chemicalStructureQuery,
      this.state.shouldHighlightSubstructure,
      this.props.searchResults
    );

    let groupByFields: Array<GroupByKey> = [];
    if (BenchlingConfig.ENABLE_SIGHTGLASS_V3_GROUP_BY) {
      groupByFields = availableGroupKeysForFilters(this.props.searchParams.filters);
      if (!hasBLASTFilter) {
        groupByFields.unshift(GroupByKey.NONE);
      }
    }

    const secondaryFilterGroups = this.props.getSecondaryFilterGroups
      ? this.props.getSecondaryFilterGroups(selectedSchema, this.props.section, folderSection)
      : getDefaultSecondaryFilterGroups(selectedSchema, this.props.section, folderSection);

    const aiAssistedSearchButton =
      BenchlingConfig.SEARCH_ENABLE_AI_ASSISTED_SEARCH &&
      // Only show Guided Search on certain listings that have been tested.
      [Section.SEARCH, Section.PROJECT_ITEMS, Section.REGISTRIES, Section.STORAGE].includes(
        this.props.section
      ) ? (
        <AIAssistedSearchButton
          availableTypes={this.props.availableTypes}
          availableFilterKeys={this.getAvailableFilterKeys(primaryFilterOptions, secondaryFilterGroups)}
          baseSearchParams={this.getClearedSearchParams()}
          onSearch={this.props.onSearch}
          onSetSecondaryDropdownOpen={this.handleSetSecondaryDropdownOpen}
        />
      ) : null;

    const filtersContainer = (
      <ListingFiltersContainer
        saveSearchButton={saveSearchButton}
        sortDropdown={this.props.showMultiKeySortMenu ? sortDropdown : null}
        primaryFilterOptions={primaryFilterOptions}
        secondaryFilterProps={{
          filterGroups: secondaryFilterGroups,
          isDropdownOpen: this.state.isSecondaryDropdownOpen,
          onSetDropdownOpen: this.handleSetSecondaryDropdownOpen,
        }}
        filters={this.props.searchParams.filters}
        filterErrors={this.props.filterErrors}
        onChangeFilters={this.handleChangeFilters}
        selectedSchema={selectedSchema}
        availableGroupByFields={groupByFields}
        availableQuickFilters={this.props.availableQuickFilters}
        // TODO(Search): remove this cast once we have all usages updated to use GroupByKey enum.
        selectedGroupByField={this.props.searchParams.groupBy?.groupField as GroupByKey}
        onChangeGroupByField={this.props.onUpdateGroupBy}
        aiAssistedSearchButton={aiAssistedSearchButton}
        clearButton={isClearEnabled ? <ClearSearchButton onClear={this.handleClear} /> : null}
      />
    );
    const settingsDropdown = (
      <SidepanelListingSettingsDropdown
        onSetSortOrder={this.handleSetSortOrder}
        onSetPageSize={this.handleSetPageSize}
        searchParams={this.props.searchParams}
        availableSortKeys={this.props.availableSortKeys}
        showSortSection={!this.props.showMultiKeySortMenu}
        displaySettingsConfig={{
          isCompact: this.props.isCompactDisplay,
          setIsCompactDisplay: this.props.setIsCompactDisplay,
        }}
      />
    );

    const savedSearchHeader = this.props.savedSearchSection ? (
      <SavedSearchHeader
        searchSection={this.props.savedSearchSection}
        onSelectSavedSearch={this.handleSelectSavedSearch}
        activeSavedSearch={this.props.activeSavedSearch}
        key="saved-searches-dropdown"
        onSearch={this.props.onSearch}
        searchParams={this.props.searchParams}
        isExpanded={this.props.isExpanded}
      />
    ) : undefined;

    const header = isSignedIn ? (
      <ListingHeader
        crumbs={this.props.makeCrumbs ? this.props.makeCrumbs(savedSearchHeader) : undefined}
        isExpanded={this.props.isExpanded}
        buttons={
          <div className="sidepanel-header-buttons u-flex-display">
            {shareSearchButton}
            {(this.props.enableToggleExpanded ?? true) ? (
              <ToggleDropdown expanded={this.props.isExpanded} />
            ) : null}
            {this.props.additionalHeaderButtons}
          </div>
        }
        queryInput={
          <QueryInput
            onUpdateQuery={this.handleSetQuery}
            query={this.props.searchParams.query}
            inputIcons={compact([
              enableBLAST ? blastInputIcons : null,
              enableChemSearch ? chemSearchInputIcon : null,
            ])}
          />
        }
        filtersContainer={filtersContainer}
        settingsDropdown={settingsDropdown}
        sectionTabs={this.props.sectionTabs}
        sectionHeader={this.props.sectionHeader}
        hideSectionTabsWhenExpanded={this.props.hideSectionTabsWhenExpanded}
        multiquerySelector={
          isBLASTMultiQuery ? (
            <MultiquerySelector
              groupIds={this.props.groups ? this.props.groups.map((group) => group.id) : []}
              groupBySelection={this.props.listingState.groupBySelection}
              setGroupBySelection={this.props.onUpdateGroupBySelection}
            />
          ) : undefined
        }
      />
    ) : (
      <ListingHeader
        crumbs={this.props.makeCrumbs ? this.props.makeCrumbs() : undefined}
        isExpanded={this.props.isExpanded}
        settingsDropdown={settingsDropdown}
        sectionTabs={this.props.sectionTabs}
        hideSectionTabsWhenExpanded={this.props.hideSectionTabsWhenExpanded}
      />
    );

    const toolbar = this.props.renderToolbar ? (
      this.props.renderToolbar(itemsWithGroupChildren, this.props.selectedIds, this.props.searchParams)
    ) : (
      <ExpandedToolbar
        actionOrigin={this.props.actionSource}
        searchSessionId={this.props.searchSessionId}
        finderId={finderId}
        items={itemsWithGroupChildren}
        selectedIds={this.props.selectedIds}
        onRefreshResults={this.props.onRefreshListing}
        folderId={folderId}
        worklistSummary={this.props.worklistSummary}
        onSaveOrder={this.props.searchParams.query ? null : this.props.onSaveOrder}
        collection={collectionFilter?.value}
        context={hasBLASTFilter ? BulkActionContext.BLAST : undefined}
      />
    );

    const additionalToolbarButtons = this.props.renderAdditionalToolbarButtons ? (
      <div
        className="u-flex-display u-vertical-center-items additional-toolbar"
        {...getTestProps({component: 'ItemListing', element: 'additionalToolbarButtons'})}
      >
        {this.props.renderAdditionalToolbarButtons(itemsWithGroupChildren, this.props.selectedIds)}
      </div>
    ) : null;

    const leadingToolbarButtons = this.props.renderLeadingToolbarButtons ? (
      <div className="u-flex-display u-vertical-center-items additional-toolbar">
        {this.props.renderLeadingToolbarButtons(itemsWithGroupChildren, this.props.selectedIds)}
      </div>
    ) : null;

    let pagination = {
      offset: this.props.searchParams.offset,
      limit: this.props.searchParams.limit,
      total: this.props.searchResults ? this.props.searchResults.total : 0,
      isExact: this.props.searchResults ? this.props.searchResults.isExact : true,
    };
    let items = this.props.items;
    let groups = this.props.groups;
    if (isBLASTMultiQuery) {
      const group = this.props.groups?.find((obj) => obj.id === this.props.listingState.groupBySelection);

      if (group != null) {
        const offset = this.props.listingState.multiqueryBlastOffset[group.id] ?? 0;
        pagination = {
          offset,
          limit: this.props.searchParams.limit,
          total: group.count,
          isExact: true,
        };
        items = group.items.slice(offset, offset + (this.props.childPageSize ?? 0));
        groups = [];
      }
    }

    const SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT = composeSidepanelSearchResultsRenderedEvent();

    return this.props.isExpanded ? (
      <nyc.Container
        className={classNames('expandedContainer', css(itemListingStyle.container))}
        size="lg"
        dataTestAttrs={{
          component: this.props.dataTestAttrs ? this.props.dataTestAttrs.component : 'ItemListing',
          element: this.props.dataTestAttrs ? this.props.dataTestAttrs.element : 'root',
          key: 'expanded',
        }}
      >
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={20}>{header}</nyc.Col>
          <nyc.Col lg={1} />
        </nyc.Row>
        <nyc.Row
          {...(this.props.sectionTabs != null
            ? {
                'aria-labelledby': `${this.props.section}_TAB`,
                id: `${this.props.section}_PANEL`,
                role: 'tabpanel',
              }
            : {})}
          fluid={true}
        >
          <nyc.Col lg={1} />
          <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
            {getConfig('ENABLE_MULTIPLE_SEARCH_RESULT_VIEWS') &&
              blastFilter?.operator != null &&
              ((this.props.items && this.props.items.length > 0) ||
                (this.props.groups && this.props.groups.length > 0)) && (
                <SecondHeaderRow
                  viewSelection={this.props.listingState.viewSelection}
                  setViewSelection={this.props.onUpdateViewSelection}
                  zoom={this.state.zoom}
                  isMultiquery={isBLASTMultiQuery}
                  groupBySelection={this.props.listingState.groupBySelection}
                  setGroupBySelection={this.props.onUpdateGroupBySelection}
                  groupIds={this.props.groups ? this.props.groups.map((group) => group.id) : []}
                  blastOperator={blastFilter.operator}
                />
              )}
            <div className="u-lr-split js-navTour-expanded u-small-spacing-top">
              <div className="u-justify-content-start">
                <div className="u-vertical-center-items">
                  <ResultsPager
                    isLoading={!this.props.searchResults || isBLASTMultiQueryLoadingChildren}
                    setOffset={this.handleSetOffset}
                    pagination={pagination}
                    subtext={this.props.searchResultsSubtext}
                  />
                  {settingsDropdown}
                  {this.renderExpandAllButton(hasBLASTFilter)}
                  {this.props.listingState.viewSelection === SearchView.ALIGNMENT &&
                    this.props.selectedIds.length > 0 && (
                      <AlignmentCompareButton
                        numSelected={this.props.selectedIds.length}
                        comparing={this.state.comparing}
                        setComparing={this.setComparing}
                      />
                    )}
                </div>
              </div>
              {this.props.listingState.viewSelection === SearchView.ALIGNMENT ? (
                <BlastViewControl
                  blastOperator={blastFilter?.operator}
                  viewMenu={{
                    coloringScheme: this.state.coloringScheme,
                    setColoringScheme: this.setColoringScheme,
                  }}
                  zoomControl={{zoom: this.state.zoom, setZoom: this.setZoom}}
                />
              ) : (
                <ListingToolbarArea>
                  {this.props.rowSelectionMode !== RowSelectionMode.SINGLE && (
                    <SelectedRowsCounter numSelected={this.props.selectedIds.length} />
                  )}
                  <div className="u-flex-display u-vertical-center-items">
                    {isSignedIn && this.props.items != null && leadingToolbarButtons}
                    {isSignedIn && this.props.items != null && toolbar}
                    {isSignedIn && this.props.items != null && additionalToolbarButtons}
                  </div>
                </ListingToolbarArea>
              )}
            </div>
            <div className="u-flex-grow u-flex-display u-justify-content-center u-small-spacing-top">
              {items == null || isBLASTMultiQueryLoadingChildren ? (
                <CenteredHorizontalThrobber />
              ) : (
                <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                  {this.props.listingState.viewSelection === SearchView.TABLE ? (
                    <ExpandedItemTable
                      actionSource={this.props.actionSource}
                      emptyMessage={this.getEmptyMessage(typesFilter, interfaceFilter)}
                      // TODO: Remove
                      finderId={finderId}
                      isCompact={this.props.isCompactDisplay}
                      isSearch={isSearch}
                      items={items}
                      groups={groups ?? []}
                      asyncFetcherIsFetchedState={this.props.asyncFetcherIsFetchedState}
                      onUpdateAsyncFetcherIsFetchedState={this.props.onUpdateAsyncFetcherIsFetchedState}
                      onSelectIds={this.props.onSelectIds}
                      onSetColumnHeaderSort={this.handleSetColumnHeaderSort}
                      onToggleExpandedItem={this.handleToggleExpandedItem}
                      onRequestMoreChildItems={this.props.onRequestMoreChildItems}
                      childPageSize={this.props.childPageSize ?? 0}
                      onToggleSelectedIds={this.handleToggleSelectedIds}
                      onOpenItem={this.handleOpenItem}
                      section={folderSection}
                      sorts={this.props.searchParams.sorts}
                      tableKey={TABLE_KEYS.EXPANDED_PROJECT_FILE_TABLE}
                      chemSearchFolderItemListingExtras={chemSearchFolderItemListingExtras}
                      defaultHiddenColumnKeys={this.props.defaultHiddenColumnKeys}
                      searchParams={this.props.searchParams}
                      collection={collectionFilter?.value}
                      searchSection={this.props.section}
                      // searchResults will be defined if this.props.items is not null.
                      searchResults={this.props.searchResults!}
                      rowSelectionMode={this.props.rowSelectionMode}
                      alwaysShowSelector={this.props.alwaysShowSelector}
                      itemMetadataById={itemMetadataById}
                    />
                  ) : this.props.listingState.viewSelection === SearchView.ALIGNMENT ? (
                    <AlignmentView
                      items={items}
                      emptyMessage={this.getEmptyMessage(typesFilter, interfaceFilter)}
                      sorts={this.props.searchParams.sorts}
                      tableKey={TABLE_KEYS.EXPANDED_PROJECT_FILE_TABLE}
                      isCompact={this.props.isCompactDisplay}
                      selectedIds={this.props.selectedIds}
                      onSetColumnHeaderSort={this.handleSetColumnHeaderSort}
                      zoom={this.state.zoom}
                      onToggleSelectedIds={this.handleToggleSelectedIds}
                      onOpenItem={this.handleOpenItem}
                      comparing={this.state.comparing}
                      finderId={finderId}
                      collection={collectionFilter?.value}
                      searchSection={this.props.section}
                      blastOperator={blastFilter?.operator}
                      coloringScheme={this.state.coloringScheme}
                    />
                  ) : (
                    <>Not yet supported</>
                  )}
                </BenchlingProfiler>
              )}
            </div>
          </nyc.Col>
          <nyc.Col lg={1} />
        </nyc.Row>
      </nyc.Container>
    ) : (
      <nyc.Container
        className={css(itemListingStyle.container)}
        size="sidepanel"
        dataTestAttrs={{
          component: this.props.dataTestAttrs ? this.props.dataTestAttrs.component : 'ItemListing',
          element: this.props.dataTestAttrs ? this.props.dataTestAttrs.element : 'root',
          key: 'collapsed',
        }}
      >
        <nyc.Row>{header}</nyc.Row>
        <nyc.Row>
          {isClearEnabled && (
            <CompactListingBanner
              total={this.props.searchResults ? pagination.total : null}
              isExact={this.props.searchResults ? this.props.searchResults.isExact : null}
              subtext={this.props.searchResultsSubtext}
            />
          )}
        </nyc.Row>
        <nyc.Row
          {...(this.props.sectionTabs != null
            ? {
                'aria-labelledby': `${this.props.section}_TAB`,
                id: `${this.props.section}_PANEL`,
                role: 'tabpanel',
              }
            : {})}
          fluid={true}
          className="u-vertical-flex"
        >
          <div className="u-flex-grow u-position-relative">
            <div
              className={classNames(
                'u-absolute-scroll-container',
                this.props.items && this.props.loadingState === LoadingState.LOADING
                  ? css(itemListingStyle.containerWithRefreshingResults)
                  : null
              )}
            >
              {items == null || isBLASTMultiQueryLoadingChildren ? (
                <CenteredHorizontalThrobber />
              ) : (
                <CompactResultsPagerWrapper pagination={pagination} setOffset={this.handleSetOffset}>
                  <BenchlingProfiler {...SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT}>
                    <SidepanelItemList
                      actionSource={this.props.actionSource}
                      emptyMessage={this.getEmptyMessage(typesFilter, interfaceFilter)}
                      finderId={finderId}
                      isCompact={this.props.isCompactDisplay}
                      activeItemId={this.props.activeItemId}
                      isSearch={isSearch}
                      items={items}
                      groups={groups ?? []}
                      asyncFetcherIsFetchedState={this.props.asyncFetcherIsFetchedState}
                      onUpdateAsyncFetcherIsFetchedState={this.props.onUpdateAsyncFetcherIsFetchedState}
                      openResult={this.handleOpenItem}
                      onToggleExpandedItems={this.props.onToggleItemsExpanded}
                      onRequestMoreChildItems={this.props.onRequestMoreChildItems}
                      childPageSize={this.props.childPageSize ?? 0}
                      folderId={folderId}
                      section={folderSection}
                      collection={collectionFilter?.value}
                    />
                  </BenchlingProfiler>
                </CompactResultsPagerWrapper>
              )}
            </div>
          </div>
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export const itemListingStyle = StyleSheet.create({
  containerWithRefreshingResults: {
    opacity: 0.5,
  },
  queryInputSearchButton: {
    order: 999,
  },
  queryInputSearchChip: {
    marginLeft: '2px',
    flex: '1 0 auto',
    order: -1,
  },
  container: {
    height: '100%',
  },
});

const ItemListingInnerComponent = ItemListingConnector.connect(ItemListing, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});

export const ITEM_LISTING_SCHEMAS_QUERY = gql`
  query ItemListingSchemasQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    ...AllAssaySchemasGraphQL
    ...AllBoxSchemasGraphQL
    ...AllConnectionSchemasGraphQL
    ...AllContainerSchemasGraphQL
    ...AllLocationSchemasGraphQL
    ...AllPlateSchemasGraphQL
    ...AllProcessTaskSchemasForSearchGraphQL
    ...AllProcessOutputSchemasForSearchGraphQL
    ...AllTagSchemasGraphQL
    ...AllStudySchemasGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('AllBoxSchemas').fragment}
  ${getFragmentWrapper('AllConnectionSchemas').fragment}
  ${getFragmentWrapper('AllContainerSchemas').fragment}
  ${getFragmentWrapper('AllLocationSchemas').fragment}
  ${getFragmentWrapper('AllPlateSchemas').fragment}
  ${getFragmentWrapper('AllProcessTaskSchemasForSearch').fragment}
  ${getFragmentWrapper('AllProcessOutputSchemasForSearch').fragment}
  ${getFragmentWrapper('AllTagSchemas').fragment}
  ${getFragmentWrapper('AllStudySchemas').fragment}
`;

export const ALL_PROCESS_TASK_SCHEMAS_QUERY = gql`
  query AllProcessTaskSchemasQuery @skipAudit @engComponent(name: WORKFLOWS) {
    ...AllProcessTaskSchemasForSearchGraphQL
  }
  ${getFragmentWrapper('AllProcessTaskSchemasForSearch').fragment}
`;

export const ALL_PROCESS_OUTPUT_SCHEMAS_QUERY = gql`
  query AllProcessOutputSchemasQuery @skipAudit @engComponent(name: WORKFLOWS) {
    ...AllProcessOutputSchemasForSearchGraphQL
  }
  ${getFragmentWrapper('AllProcessOutputSchemasForSearch').fragment}
`;

export const ALL_STUDY_SCHEMAS_QUERY = gql`
  query AllStudySchemasQuery @skipAudit @engComponent(name: STUDIES) {
    ...AllStudySchemasGraphQL
  }
  ${getFragmentWrapper('AllStudySchemas').fragment}
`;

export const ITEM_LISTING_QUERY = gql`
  query ItemListingQuery(
    $analysisIds: [StrictString!]!
    $analyticsDashboardIds: [StrictString!]!
    $bioentityIds: [StrictString!]!
    $bulkAssemblyIds: [StrictString!]!
    $connectionIds: [StrictString!]!
    $datasetIds: [StrictString!]!
    $entryIds: [StrictString!]!
    $fileIds: [StrictString!]!
    $folderIds: [StrictString!]!
    $jellyfishIds: [StrictString!]!
    $processIds: [StrictString!]!
    $processOutputIds: [StrictString!]!
    $processTaskIds: [StrictString!]!
    $proteinAlignmentIds: [StrictString!]!
    $protocolIds: [StrictString!]!
    $requestIds: [StrictString!]!
    $requestV2SubmissionIds: [StrictString!]!
    $runIds: [StrictString!]!
    $sequenceAlignmentIds: [StrictString!]!
    $stageEntryIds: [StrictString!]!
    $studyIds: [StrictString!]!
    $storableIds: [StrictString!]!
    $workflowIds: [StrictString!]!
    $worklistIds: [StrictString!]!
    $worksheetIds: [StrictString!]!
    $templateCollectionIds: [StrictString!]!
    $testDefinitionIds: [StrictString!]!
    $testOrderIds: [StrictString!]!
    $collectibleIds: [StrictString!]!
    $reactionIds: [StrictString!]!
  ) @skipAudit @engComponent(name: SIGHTGLASS) {
    analyses(ids: $analysisIds) {
      ...AnalysisSearchAttributesGraphQL
      ...InaccessibleInfoGraphQL
    }
    analyticsDashboards(ids: $analyticsDashboardIds) {
      ...AnalyticsDashboardSearchAttributesGraphQL
      ...InaccessibleInfoGraphQL
    }
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
    }
    bulkAssemblies(ids: $bulkAssemblyIds) {
      ...BulkAssemblyListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    collectibles(ids: $collectibleIds) {
      ...CollectibleListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    connections(ids: $connectionIds) {
      ...ConnectionListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    datasets(ids: $datasetIds) {
      ...DatasetListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    entries(ids: $entryIds) {
      ...EntrySummaryOrInaccessibleGraphQL
    }
    files(ids: $fileIds) {
      ...FileListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    folders(ids: $folderIds) {
      ...FolderWithPermissionsGraphQL
      ...InaccessibleInfoGraphQL
    }
    jellyfishes(ids: $jellyfishIds) {
      ...JellyfishListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    processes(ids: $processIds) {
      ...ProcessListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    processOutputs(ids: $processOutputIds) {
      ...ProcessOutputListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    processTasks(ids: $processTaskIds) {
      ...ProcessTaskListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    proteinAlignments(ids: $proteinAlignmentIds) {
      ...ProteinAlignmentSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
    protocols(ids: $protocolIds) {
      ...ProtocolSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
    reactions(ids: $reactionIds) {
      ...ReactionGraphQL
      ...InaccessibleInfoGraphQL
    }
    requests(ids: $requestIds) {
      ...RequestGraphQL
      ...InaccessibleInfoGraphQL
    }
    requestV2Submissions(ids: $requestV2SubmissionIds) {
      ...RequestV2SubmissionListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    runs(ids: $runIds) {
      ...RunGraphQL
      ...InaccessibleInfoGraphQL
    }
    sequenceAlignments(ids: $sequenceAlignmentIds) {
      ...SequenceAnalysisSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
    stageEntries(ids: $stageEntryIds) {
      ...StageEntrySummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        ...StorableSummaryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    studies(ids: $studyIds) {
      ...StudyListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    templateCollectionsByIds(ids: $templateCollectionIds) {
      ...TemplateCollectionListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    testDefinitions(ids: $testDefinitionIds) {
      ...TestDefinitionListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    testOrders(ids: $testOrderIds) {
      ...TestOrderListingGraphQL
      ...InaccessibleInfoGraphQL
    }
    workflows(ids: $workflowIds) {
      ...WorkflowSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
    worklists(ids: $worklistIds) {
      ...WorklistSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
    worksheets(ids: $worksheetIds) {
      ...WorksheetListingGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('AnalysisSearchAttributes').fragment}
  ${getFragmentWrapper('AnalyticsDashboardSearchAttributes').fragment}
  ${getFragmentWrapper('BaseCollectibleSummary').fragment}
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('BulkAssemblyListing').fragment}
  ${getFragmentWrapper('CollectibleListing').fragment}
  ${getFragmentWrapper('ConnectionListing').fragment}
  ${getFragmentWrapper('DatasetListing').fragment}
  ${getFragmentWrapper('EntrySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('FileListing').fragment}
  ${getFragmentWrapper('FolderWithPermissions').fragment}
  ${getFragmentWrapper('JellyfishListing').fragment}
  ${getFragmentWrapper('ProcessListing').fragment}
  ${getFragmentWrapper('ProcessOutputListing').fragment}
  ${getFragmentWrapper('ProcessTaskListing').fragment}
  ${getFragmentWrapper('ProteinAlignmentSummary').fragment}
  ${getFragmentWrapper('ProtocolSummary').fragment}
  ${getFragmentWrapper('Reaction').fragment}
  ${getFragmentWrapper('Request').fragment}
  ${getFragmentWrapper('RequestV2SubmissionListing').fragment}
  ${getFragmentWrapper('Run').fragment}
  ${getFragmentWrapper('SequenceAnalysisSummary').fragment}
  ${getFragmentWrapper('StageEntrySummary').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
  ${getFragmentWrapper('StudyListing').fragment}
  ${getFragmentWrapper('TemplateCollectionListing').fragment}
  ${getFragmentWrapper('TestDefinitionListing').fragment}
  ${getFragmentWrapper('TestOrderListing').fragment}
  ${getFragmentWrapper('WorkflowSummary').fragment}
  ${getFragmentWrapper('WorklistSummary').fragment}
  ${getFragmentWrapper('WorksheetListing').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const EMPTY_SCHEMAS: ItemListingSchemasQuery = {
  __typename: 'Query',
  boxSchemas: [],
  connectionSchemas: [],
  containerSchemas: [],
  locationSchemas: [],
  plateSchemas: [],
  processTaskSchemasSupportingPlatformFeature: [],
  processOutputSchemasSupportingPlatformFeature: [],
  tagSchemas: [],
  studySchemas: [],
  requestSchemas: [],
  resultSchemas: [],
  runSchemas: [],
};

export const ITEM_LISTING_INTERFACES_QUERY = gql`
  query ItemListingInterfacesQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    ...AllSchemaInterfacesGraphQL
  }
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
`;

/**
 * This is a wrapper around ItemListing to aid in the migration from using a Data Connector to using
 * GraphQL. As much as possible, it passes down datatypes that mirror the ones we already use, so combining
 * the GraphQL results with the not yet migrated results is kept simple. As more and more of the data
 * fetching moves to GraphQL, this component will get larger and the ItemListing data connector will shrink,
 * until we replace it entirely.
 *
 * Most of the cache checking is done automatically by the Apollo client. We do have to give the
 * client a mapping function to tell it which cache keys to look for, in typePolicies.ts. The only place
 * where we need to explicitly check the cache ourselves is to read schemas, since we cannot fire a query
 * before we have any search results, but we should still make an attempt to pass the schemas down.
 *
 * To add a new schema type, follow these steps:
 * 1. Update ITEM_LISTING_SCHEMAS_QUERY
 * 2. Update queryResultsToSchemas
 * 3. Remove that key for that type in the map in schemasForAllTypes above
 *
 * To add a new item type, follow these steps:
 * 1. Update ITEM_LISTING_QUERY
 * 3. Add an entry to typePolicies.ts that, given ids, provides Apollo with a cache key for each item.
 * 4. Update idsToItemListingQueryParams and queryResultsToItems
 * 5. Remove the corresponding fetching function in the data connector
 */
export default function ItemListingGraphQLWrapper(props: ItemListingProps & SearchProps): JSX.Element | null {
  // Group children are items, so we need to include the child ids in the query params as well.
  const groupChildIds = props.searchResults?.groups
    ? props.searchResults.groups.map((group) => group.childIds || [])
    : [];

  const groupItemIds = props.searchResults?.groupItemIds ?? [];

  const itemListingQueryParams = props.searchResults
    ? idsToItemListingQueryParams(props.searchResults.ids.concat(...groupChildIds, ...groupItemIds))
    : idsToItemListingQueryParams([]);

  let schemasData = EMPTY_SCHEMAS;
  if (getConfig('SEARCH_ENABLE_SPLIT_ITEM_LISTING_QUERY')) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const assaySchemasResult = useBenchlingQuery<AllAssaySchemasQuery>(ALL_ASSAY_SCHEMAS_QUERY);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const connectionSchemasResult = useBenchlingQuery<AllConnectionSchemasQuery>(
      ALL_CONNECTION_SCHEMAS_QUERY
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const inventorySchemasResult = useBenchlingQuery<AllInventorySchemasQuery>(ALL_INVENTORY_SCHEMAS_QUERY);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const processOutputSchemasResult = useBenchlingQuery<AllProcessOutputSchemasQuery>(
      ALL_PROCESS_OUTPUT_SCHEMAS_QUERY
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const processTaskSchemasResult = useBenchlingQuery<AllProcessTaskSchemasQuery>(
      ALL_PROCESS_TASK_SCHEMAS_QUERY
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const studiesSchemasResult = useBenchlingQuery<AllStudySchemasQuery>(ALL_STUDY_SCHEMAS_QUERY);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const tagSchemasResult = useBenchlingQuery<AllTagSchemasQuery>(ALL_TAG_SCHEMAS_QUERY);

    if (
      assaySchemasResult.type === QueryResultType.SUCCESS &&
      connectionSchemasResult.type === QueryResultType.SUCCESS &&
      inventorySchemasResult.type === QueryResultType.SUCCESS &&
      processOutputSchemasResult.type === QueryResultType.SUCCESS &&
      processTaskSchemasResult.type === QueryResultType.SUCCESS &&
      studiesSchemasResult.type === QueryResultType.SUCCESS &&
      tagSchemasResult.type === QueryResultType.SUCCESS
    ) {
      schemasData = {
        ...assaySchemasResult.data,
        ...connectionSchemasResult.data,
        ...inventorySchemasResult.data,
        ...processOutputSchemasResult.data,
        ...processTaskSchemasResult.data,
        ...studiesSchemasResult.data,
        ...tagSchemasResult.data,
      };
    }
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const schemasResult = useBenchlingQuery<ItemListingSchemasQuery>(ITEM_LISTING_SCHEMAS_QUERY);
    if (schemasResult.type === QueryResultType.SUCCESS) {
      schemasData = schemasResult.data;
    }
  }

  const itemListingResult = useSkippableBenchlingQuery<ItemListingQuery, ItemListingQueryVariables>(
    ITEM_LISTING_QUERY,
    {skip: !props.searchResults, variables: itemListingQueryParams}
  );

  const interfacesResult = useBenchlingQuery<ItemListingInterfacesQuery>(ITEM_LISTING_INTERFACES_QUERY);

  // We aren't refetching the various schema queries on every search, because the schemas aren't likely to change often,
  // and it's not worth the extra expensive network requests on every search.
  // There's also no need to refetch ITEM_LISTING_QUERY because the global search endpoint will refetch it and write it to the cache.

  const schemasFromQuery = queryResultsToSchemas(schemasData, props.filterSchemas);
  const itemsFromQuery =
    itemListingResult.type === QueryResultType.SUCCESS ? queryResultsToItems(itemListingResult.data) : null;
  const interfacesFromQuery =
    interfacesResult.type === QueryResultType.SUCCESS ? interfacesResult.data.schemaInterfaces : null;

  const schemaInterfaces = interfacesFromQuery
    ? interfacesFromQuery.map((schemaInterface) =>
        mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(schemaInterface)
      )
    : [];

  return (
    <ItemListingInnerComponent
      {...props}
      graphqlItems={itemsFromQuery}
      graphqlSchemasByAvailableType={schemasFromQuery}
      schemaInterfaces={schemaInterfaces}
    />
  );
}
